import { AfterViewInit, Component, ElementRef, inject, OnDestroy, ViewChild } from '@angular/core';

import { JwtAuth } from 'src/app/core/models/jwt-auth';
import { ApiError } from 'src/app/core/models/api-error';
import { environment } from 'src/environments/environment';
import WebViewer, { WebViewerInstance } from '@pdftron/webviewer';
import { ViewerDocument } from 'src/app/core/types/viewer-document';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { ViewerService } from 'src/app/core/services/viewer/viewer.service';
import { NotificationService } from 'src/app/core/services/notification/notification.service';

const BASE_URL = `${environment.service.url}/api`;

@Component({
  selector: 'shared-viewer',
  templateUrl: './viewer.component.html'
})
export class ViewerComponent implements AfterViewInit, OnDestroy {

  private readonly _authService = inject(AuthService);
  private readonly _viewerService = inject(ViewerService);
  private readonly _notificationService = inject(NotificationService);

  /**
   * Instancia de Apryse WebViewer.
   */
  private _webViewerInstance?: WebViewerInstance;

  /**
   * Indica si el componente está siendo destruido.
   */
  private _isDestroyed = false;

  public isLoaded = false;
  public isLoading = true;

  @ViewChild('viewer', { static: false })
  private viewer?: ElementRef<HTMLDivElement>;

  ngAfterViewInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this._isDestroyed = true;
    this.destroyViewer();
  }

  public get isOpen(): boolean {
    return this._viewerService.isOpen;
  }

  public get isClosing(): boolean {
    return this._viewerService.isClosing;
  }

  public get document(): ViewerDocument | undefined {
    return this._viewerService.document;
  }

  // ==========================================
  // INITIALIZATION
  // ==========================================

  public initializeComponent(): void {

    this._authService
      .generateJwtAccessToken$()
      .subscribe({
        next: (response: JwtAuth) => {
          // El componente pudo haber sido destruido
          // mientras se generaba el token.
          if (this._isDestroyed) {
            return;
          }

          let url = '';
          if (this.document?.manual) {
            url = `${BASE_URL}/settings/get-manual?token=${response.token}`;
          } else {
            url = `${BASE_URL}/documents/${this.document?.folderId}/${this.document?.id}/content?token=${response.token}`;
          }

          // ==========================================
          // VALIDAR VIEWER
          // ==========================================
          if (!this.viewer?.nativeElement) {
            console.warn('ViewerComponent: el elemento #viewer no está disponible.');
            this.isLoading = false;
            return;
          }

          // ==========================================
          // INICIALIZAR APRYSE
          // ==========================================
          WebViewer(
            {
              path: '/lib/webviewer',
              licenseKey: 'demo:1787787810393:639cc99f030000000015bf2c4762c33450d418888a12c9815168608ffc',
              initialDoc: url
            },
            this.viewer.nativeElement
          )
            .then(instance => {

              if (this._isDestroyed) {
                this.destroyApryseInstance(instance);
                return;
              }

              console.log('Apryse WebViewer inicializado');

              this._webViewerInstance = instance;

              const { UI, Core } = instance;
              const { Feature } = UI;

              // ==========================================
              // CONFIGURACIÓN DEL DOCUMENT VIEWER
              // ==========================================
              Core.annotationManager.enableReadOnlyMode();

              // ==========================================
              // AGREGAR TÍTULO DEL DOCUMENTO
              // ==========================================
              this.addDocumentTitle(instance);

              // ==========================================
              // AGREGAR BOTÓN CERRAR
              // ==========================================
              this.addCloseButton(instance);

              // ==========================================
              // DOCUMENT LOADED
              // ==========================================
              const documentViewer = Core.documentViewer;

              documentViewer.addEventListener('documentLoaded', () => {
                if (this._isDestroyed) {
                  return;
                }

                console.log('Documento cargado en Apryse');

                // ==========================================
                // ZOOM
                // ==========================================
                UI.setZoomLevel('100%');

                // ==========================================
                // FEATURES
                // ==========================================
                UI.disableFeatures([
                  Feature.InlineComment,
                  Feature.Download,
                  Feature.Print
                ]);

                // ==========================================
                // ELEMENTOS
                // ==========================================
                UI.disableElements([
                  'saveButton',
                  'saveAsButton',
                  'downloadButton',
                  'settingsButton'
                ]);

                // ==========================================
                // ESTADO
                // ==========================================
                this.isLoaded = true;
                this.isLoading = false;
              }
              );
            })
            .catch(error => {
              // Si Angular ya destruyó el componente,
              // no intentamos modificar su estado.
              if (this._isDestroyed) {
                return;
              }
              console.error('Error inicializando Apryse WebViewer:', error);
              this.isLoading = false;
            });
        },
        error: (error: ApiError) => {
          if (this._isDestroyed) {
            return;
          }
          this.isLoading = false;
          this.closeViewerHandler();
          this._notificationService.warning('Ocurrió un error', error.message);
        }
      });
  }

  /**
   * Event handler para cerrar el Viewer.
   */
  public closeViewerHandler(): void {
    if (this._isDestroyed) {
      return;
    }
    this._viewerService.close();
  }

  /**
   * Agrega el botón "Cerrar" al header de
   * Modular UI.
   */
  private addCloseButton(instance: WebViewerInstance): void {

    if (this._isDestroyed) {
      return;
    }

    const closeButton =
      new instance.UI.Components.CustomButton({
        dataElement: 'closeViewerButton',
        title: 'Cerrar',
        label: 'Cerrar',
        img: 'icon-close',
        onClick: () => {
          if (this._isDestroyed) {
            return;
          }
          this.closeViewerHandler();
        }
      });

    const header = instance.UI.getModularHeader('default-top-header');

    header.setItems([...header.getItems(), closeButton]);
  }

  private addDocumentTitle(instance: WebViewerInstance): void {
    if (this._isDestroyed) {
      return;
    }
    const documentTitle = new instance.UI.Components.Label({
      dataElement: 'documentTitleLabel',
      label: this.document?.title ?? 'Documento'
    });

    const header = instance.UI.getModularHeader('default-top-header');

    header.setItems([documentTitle, ...header.getItems()]);
  }

  /**
   * Destruye la instancia de Apryse.
   */
  private destroyViewer(): void {
    if (!this._webViewerInstance) {
      return;
    }
    try {
      this.destroyApryseInstance(this._webViewerInstance);
    } catch (error) {
      console.warn('Error destruyendo Apryse WebViewer:', error);
    } finally {
      this._webViewerInstance = undefined;
      this.isLoaded = false;
      this.isLoading = false;
    }
  }

  /**
   * Limpieza de la instancia de Apryse.
   */
  private destroyApryseInstance(instance: WebViewerInstance): void {
    try {
      instance.UI.dispose();
    } catch (error) {
      console.warn('No fue posible hacer dispose de Apryse:', error);
    }
  }
}